/**
 * Reports when the editor's legacy lifecycle and the Remix state machine settle on different
 * phases. This is a read-only observer: it derives nothing the editor does not already compute for
 * itself, and it never participates in editor behavior.
 *
 * Classification lives in `editorRemixParity.ts`; this hook owns only scoping, settle timing and
 * deduplication. The editor reports its own phase through the lifecycle adapter, and the machine
 * phase is read imperatively from the registry rather than through `useMachinePhase`, so machine
 * transitions add no renders to the editor subtree.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { EditorRemixLifecyclePhase } from '@atlassian/malleable-ui-components/types';
import {
	deriveMachineEditorRemixPhase,
	isTerminalEditorRemixPhase,
} from '@atlassian/malleable-ui-components/editor-remix/lifecycle';
import {
	getRemixMachine,
	subscribeToMachine,
} from '@atlassian/malleable-ui-components/state/machine';

import {
	createDivergenceSignature,
	dispatchEditorRemixParityEvent,
} from './dispatchEditorRemixParityEvent.ts';
import { compareEditorRemixParity, type EditorRemixProducerKind } from './editorRemixParity.ts';

/**
 * How long a non-terminal phase pair must hold before it counts as settled.
 *
 * The editor's own state and the machine's watcher-driven phase land on different ticks, so a
 * healthy flow briefly reports two different phases at every transition. Reporting only what
 * survives this delay keeps that ordinary skew out of the signal.
 *
 * Matches the renderer's window so both surfaces are equally sensitive.
 */
const PARITY_SETTLE_MS = 250;

type ObservedFlow = {
	remixId: string;
	producerKind: EditorRemixProducerKind;
	legacyPhase: EditorRemixLifecyclePhase;
	reportedSignatures: Set<string>;
	unsubscribe: () => void;
	settleTimeout: ReturnType<typeof setTimeout> | undefined;
};

export type EditorRemixParityDiagnostics = Readonly<{
	/** Begins observing an admitted flow. Replaces any flow still being observed. */
	observeFlow: (remixId: string, producerKind: EditorRemixProducerKind) => void;
	/**
	 * Stops observing. A pair still awaiting settlement is classified only when the machine ended on
	 * a terminal outcome; anything still legitimately in flight is discarded.
	 */
	releaseFlow: (remixId: string) => void;
	/**
	 * Accepts the editor's own lifecycle phase. Recorded even when no flow is being observed, so the
	 * next admitted flow can be seeded from it.
	 */
	recordLegacyPhase: (legacyPhase: EditorRemixLifecyclePhase) => void;
}>;

/**
 * Runs an observation step, swallowing anything it throws.
 *
 * Every entry point is wrapped in this. Diagnostics are called from inside the admission and
 * settlement paths, so an observer that threw would turn a healthy flow into a failed one — the one
 * thing this must never do. There is no product behavior to fall back to, so a failed observation
 * is simply not reported.
 */
const safely = (observation: () => void): void => {
	try {
		observation();
	} catch {
		// Diagnostics must never affect the editor flow.
	}
};

export const useEditorRemixParityDiagnostics = (): EditorRemixParityDiagnostics => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const observedRef = useRef<ObservedFlow | undefined>(undefined);
	/**
	 * The editor's most recent phase, tracked whether or not a flow is being observed.
	 *
	 * Both producers turn thinking on before they ask for admission, so the editor is already at
	 * `thinking` by the time a flow arrives here. Seeding a new flow at `inactive` instead would
	 * strand it there — the reporter dedupes on its side and will not resend a phase it has already
	 * sent — producing a false `legacy-not-tracking` divergence on an ordinary flow.
	 */
	const latestLegacyPhaseRef = useRef<EditorRemixLifecyclePhase>('inactive');

	const clearSettleTimeout = useCallback((flow: ObservedFlow): void => {
		if (flow.settleTimeout !== undefined) {
			clearTimeout(flow.settleTimeout);
			flow.settleTimeout = undefined;
		}
	}, []);

	const stopObserving = useCallback(
		(flow: ObservedFlow): void => {
			clearSettleTimeout(flow);
			flow.unsubscribe();
			if (observedRef.current === flow) {
				observedRef.current = undefined;
			}
		},
		[clearSettleTimeout],
	);

	const reportDivergence = useCallback(
		(flow: ObservedFlow, machinePhase: EditorRemixLifecyclePhase): void => {
			const settled = compareEditorRemixParity({
				producerKind: flow.producerKind,
				legacyPhase: flow.legacyPhase,
				machinePhase,
				isStable: true,
			});
			if (settled.outcome !== 'divergence') {
				return;
			}

			// Each admitted flow reports a given divergence once, so a divergence that persists
			// across many transitions does not become a flood of identical events.
			const signature = createDivergenceSignature(settled.divergence);
			if (flow.reportedSignatures.has(signature)) {
				return;
			}
			flow.reportedSignatures.add(signature);

			dispatchEditorRemixParityEvent(createAnalyticsEvent, settled.divergence);
		},
		[createAnalyticsEvent],
	);

	/**
	 * Classifies a pair still pending when observation ends.
	 *
	 * Convergence is the normal case: in shadow the machine drives nothing, so teardown only follows
	 * the editor reaching its own terminal phase, which the machine then matches. The one case worth
	 * keeping is the pair that never converged and is now out of time — the machine settled on one
	 * terminal outcome while the editor settled on the other.
	 *
	 * Both sides must be terminal to report. A terminal machine against a non-terminal editor is the
	 * same skew the settle window exists for: the machine subscriber runs synchronously on the
	 * watcher's transition while the editor's own terminal state needs a React commit, and an
	 * ordinary error tears the Remix UI down inside that window. Reporting on the machine side alone
	 * would fire on every one of those.
	 *
	 * Read the pending flag before `stopObserving`, which clears it.
	 */
	const classifyUnsettledPairAtTeardown = useCallback(
		(flow: ObservedFlow): void => {
			if (flow.settleTimeout === undefined) {
				return;
			}

			const machinePhase = deriveMachineEditorRemixPhase(getRemixMachine(flow.remixId)?.state());
			if (
				isTerminalEditorRemixPhase(machinePhase) &&
				isTerminalEditorRemixPhase(flow.legacyPhase)
			) {
				reportDivergence(flow, machinePhase);
			}
		},
		[reportDivergence],
	);

	/**
	 * Classifies the flow's current pair, deferring anything that has not settled.
	 *
	 * Every differing pair waits out the settle window, including a terminal machine phase. The
	 * machine subscriber fires synchronously on the watcher's transition while the editor's own
	 * error and abort state needs a React commit, so reporting a terminal machine phase on sight
	 * would fire a `terminal-outcome-mismatch` on every ordinary error. A pair that is genuinely
	 * stuck is caught by `classifyUnsettledPairAtTeardown` instead.
	 */
	const evaluate = useCallback(
		(flow: ObservedFlow): void => {
			clearSettleTimeout(flow);

			const machinePhase = deriveMachineEditorRemixPhase(getRemixMachine(flow.remixId)?.state());
			const result = compareEditorRemixParity({
				producerKind: flow.producerKind,
				legacyPhase: flow.legacyPhase,
				machinePhase,
				isStable: false,
			});

			if (result.outcome === 'match' || result.outcome === 'pending') {
				if (result.outcome === 'pending' && result.reason === 'awaiting-stable-phases') {
					flow.settleTimeout = setTimeout(() => {
						flow.settleTimeout = undefined;
						// Re-read both sides: the pair that settles is the one worth reporting, and either
						// side may have moved while the window was open.
						const settledMachinePhase = deriveMachineEditorRemixPhase(
							getRemixMachine(flow.remixId)?.state(),
						);
						reportDivergence(flow, settledMachinePhase);
					}, PARITY_SETTLE_MS);
				}
				return;
			}

			reportDivergence(flow, machinePhase);
		},
		[clearSettleTimeout, reportDivergence],
	);

	const observeFlow = useCallback(
		(remixId: string, producerKind: EditorRemixProducerKind): void => {
			safely(() => {
				const previous = observedRef.current;
				if (previous) {
					// A newly admitted flow supersedes the previous one. Ending observation is not proof
					// of divergence, so the retained pair is discarded without classification.
					stopObserving(previous);
				}

				const flow: ObservedFlow = {
					remixId,
					producerKind,
					// Seeded from the editor's current phase, not from `inactive`: thinking is already on
					// by the time admission happens.
					legacyPhase: latestLegacyPhaseRef.current,
					reportedSignatures: new Set<string>(),
					unsubscribe: () => {},
					settleTimeout: undefined,
				};
				flow.unsubscribe = subscribeToMachine(remixId, () => {
					safely(() => {
						if (observedRef.current === flow) {
							evaluate(flow);
						}
					});
				});
				observedRef.current = flow;
				// Admission is itself a comparison point. Waiting for the next machine transition would
				// miss a pair that is already mismatched the moment the flow is admitted.
				evaluate(flow);
			});
		},
		[evaluate, stopObserving],
	);

	const releaseFlow = useCallback(
		(remixId: string): void => {
			safely(() => {
				const flow = observedRef.current;
				if (flow?.remixId !== remixId) {
					return;
				}
				classifyUnsettledPairAtTeardown(flow);
				stopObserving(flow);
			});
		},
		[classifyUnsettledPairAtTeardown, stopObserving],
	);

	const recordLegacyPhase = useCallback(
		(legacyPhase: EditorRemixLifecyclePhase): void => {
			safely(() => {
				// Recorded unconditionally so the next admitted flow can be seeded from it, even when
				// this phase arrives before any flow exists.
				latestLegacyPhaseRef.current = legacyPhase;

				const flow = observedRef.current;
				if (!flow || flow.legacyPhase === legacyPhase) {
					return;
				}
				flow.legacyPhase = legacyPhase;
				evaluate(flow);
			});
		},
		[evaluate],
	);

	useEffect(
		() => () => {
			safely(() => {
				const flow = observedRef.current;
				if (flow) {
					classifyUnsettledPairAtTeardown(flow);
					stopObserving(flow);
				}
			});
		},
		[classifyUnsettledPairAtTeardown, stopObserving],
	);

	return useMemo(
		() => ({ observeFlow, releaseFlow, recordLegacyPhase }),
		[observeFlow, recordLegacyPhase, releaseFlow],
	);
};
